import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  type GestureResponderEvent,
  Platform,
  Pressable,
  View,
} from 'react-native';
import { Menu, Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';

/**
 * Per-message overflow menu shown next to the author, mirroring Gripp OS:
 * on row hover the trailing slot widens (sliding the timestamp left) and an
 * orange circle with white dots pops in; clicking opens a Crop Circle–styled
 * menu of actions. On native there is no hover, so the circle is always present
 * as a touch target.
 *
 * Actions are optional so an absent handler hides its row, and that is the
 * whole mechanism for varying the menu per surface. Today:
 *
 * - a chat **feed row** gets Thread | Side chat | Call | Copy | Share, minus
 *   Copy on a compact line (no body text for it to act on) and minus Side
 *   chat where the activity has no anchor message to parent one to;
 * - a row **inside a thread or side chat** gets Call | Copy, because a
 *   sub-conversation never spawns another;
 * - a row in the classic activity thread gets Side chat | Call | Copy.
 *
 * The caller mounts it only when at least one action is available.
 *
 * Lifted out of `issueMessage.tsx`, where it was a private component, so the
 * asset chat feed's rows could use the same menu instead of growing a second
 * one that would drift from it.
 */
export const MessageActionMenu = ({
  hovered,
  messageName,
  onThread,
  onSideChat,
  onCall,
  onCopy,
  onShare,
  testID,
}: {
  hovered: boolean;
  messageName: string;
  /**
   * The row's own conversation. Feed rows only: a row with no replies has no
   * pill, so this is that row's only route into its thread — which is why the
   * chat feed's menu is load-bearing rather than a garnish. Absent inside a
   * thread or side chat, where a sub-conversation never spawns another.
   */
  onThread?: () => void;
  onSideChat?: () => void;
  onCall?: () => void;
  onCopy?: () => void;
  /** Web only; there is no mobile share panel yet, so mobile passes nothing. */
  onShare?: () => void;
  testID?: string;
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // Tracked in addition to the row's own hover, and this is load-bearing
  // rather than redundant. The trigger's visibility is driven by the ROW's
  // hover, but the trigger sits inside the row and is only 28px wide: any
  // moment the row's hover drops while the pointer is on the trigger hides it,
  // which drops the pointer back onto the row, which re-enters, which shows it
  // again — a visible flicker exactly when someone is trying to click it.
  // Holding the trigger visible while the pointer is on the trigger itself
  // breaks that loop whatever caused the row's hover to lapse, and costs no
  // layout change.
  const [selfHovered, setSelfHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  // The trigger collapses to zero width until it is wanted, so a keyboard
  // user would otherwise tab onto an invisible clipped button — and this menu
  // is the row's documented keyboard route into its thread, so that route has
  // to be discoverable. Focus expands it exactly as hover does.
  const [focused, setFocused] = useState(false);
  const isWeb = Platform.OS === 'web';

  // Present while the row or the trigger is hovered, while it is being
  // pressed, or while the menu is open; always present on native.
  const show = !isWeb || hovered || selfHovered || pressed || focused || visible;

  // Two staged values: the slot width opens first, then the circle pops in.
  const slot = useRef(new Animated.Value(show ? 1 : 0)).current;
  const circle = useRef(new Animated.Value(show ? 1 : 0)).current;

  useEffect(() => {
    if (!isWeb) return;
    if (show) {
      Animated.parallel([
        Animated.timing(slot, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(circle, {
          toValue: 1,
          duration: 150,
          delay: 80,
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(circle, {
          toValue: 0,
          duration: 120,
          useNativeDriver: false,
        }),
        Animated.timing(slot, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [isWeb, show, slot, circle]);

  const slotWidth = isWeb
    ? slot.interpolate({ inputRange: [0, 1], outputRange: [0, 32] })
    : 32;
  const circleScale = isWeb
    ? circle.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] })
    : 1;

  const trigger = (
    <Animated.View style={[styles.callSlot, { width: slotWidth }]}>
      <Animated.View
        style={{
          opacity: isWeb ? circle : 1,
          transform: [{ scale: circleScale }],
        }}
      >
        <Pressable
          onHoverIn={() => setSelfHovered(true)}
          onHoverOut={() => setSelfHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onPressIn={() => setPressed(true)}
          onPressOut={() => setPressed(false)}
          // Stops at the trigger rather than reaching an enclosing Pressable.
          // On RN-Web a nested press fires both handlers (the press dispatches
          // a real DOM click that bubbles, unlike native's responder
          // negotiation), so on the chat feed — whose whole row opens the
          // thread — pressing this opened the thread INSTEAD of the menu, and
          // the row unmounting under the cursor read as the trigger flickering.
          onPress={(event?: GestureResponderEvent) => {
            event?.stopPropagation?.();
            setVisible(true);
          }}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('chat.row.menu.more', { name: messageName })}
          testID={testID}
          style={({ pressed }) => [
            styles.callCircle,
            pressed && styles.callCirclePressed,
          ]}
        >
          <View style={styles.dotsRow}>
            <View style={styles.dot} />
            <View style={styles.dot} />
            <View style={styles.dot} />
          </View>
        </Pressable>
      </Animated.View>
    </Animated.View>
  );

  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      contentStyle={styles.menu}
      anchor={trigger}
    >
      {onThread && (
        <ActionMenuRow
          iconName="Comment"
          label={t('chat.row.menu.thread')}
          accessibilityLabel={t('chat.row.menu.thread')}
          onPress={() => {
            setVisible(false);
            onThread();
          }}
          testID={testID ? `${testID}-thread` : undefined}
        />
      )}
      {onSideChat && (
        <ActionMenuRow
          iconName="Sidechat"
          label={t('chat.row.menu.sideChat')}
          accessibilityLabel={t('chat.row.menu.sideChat')}
          onPress={() => {
            setVisible(false);
            onSideChat();
          }}
          testID={testID ? `${testID}-sidechat` : undefined}
        />
      )}
      {onCall && (
        <ActionMenuRow
          iconName="Call"
          label={t('chat.row.menu.call')}
          accessibilityLabel={t('chat.row.menu.call')}
          onPress={() => {
            setVisible(false);
            onCall();
          }}
          testID={testID ? `${testID}-call` : undefined}
        />
      )}
      {onCopy && (
        <ActionMenuRow
          iconName="Copy"
          label={t('chat.thread.copy')}
          accessibilityLabel={t('chat.thread.copy')}
          onPress={() => {
            setVisible(false);
            onCopy();
          }}
          testID={testID ? `${testID}-copy` : undefined}
        />
      )}
      {onShare && (
        <ActionMenuRow
          iconName="Share"
          label={t('chat.row.menu.share')}
          accessibilityLabel={t('chat.row.menu.share')}
          onPress={() => {
            setVisible(false);
            onShare();
          }}
          testID={testID ? `${testID}-share` : undefined}
        />
      )}
    </Menu>
  );
};

/**
 * A single Crop Circle menu row (icon + label). Mirrors Gripp OS's PopupItem:
 * 32px tall, 12px horizontal padding, 12px gap between a 16px icon and the
 * label, with a neutral highlight on hover. We render this instead of Paper's
 * `Menu.Item` because that reserves a wide fixed icon column, leaving an
 * oversized gap between the icon and the text.
 *
 * Exported so other Crop Circle overflow menus (the asset chat header's
 * kebab menu in `assetChatBand.tsx`) render identical rows instead of
 * growing a second implementation of the same visual.
 */
export const ActionMenuRow = ({
  iconName,
  label,
  accessibilityLabel,
  onPress,
  testID,
}: {
  iconName: 'Call' | 'Sidechat' | 'Copy' | 'Comment' | 'Share';
  label: string;
  accessibilityLabel: string;
  onPress: () => void;
  testID?: string;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      style={[styles.menuItem, hovered && styles.menuItemHovered]}
    >
      <Icon name={iconName} size={16} color={Colors.role.textStrong} />
      <Text style={styles.menuItemTitle}>{label}</Text>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  // Trailing slot that opens on hover (web) to make room for the circle,
  // sliding the timestamp left — mirrors Gripp OS.
  callSlot: {
    height: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    overflow: 'hidden',
  },
  callCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.brand.core,
  },
  callCirclePressed: {
    opacity: 0.85,
  },
  dotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2.5,
  },
  dot: {
    width: 2.8,
    height: 2.8,
    borderRadius: 1.4,
    backgroundColor: role.surface,
  },
  // Crop Circle overflow menu surface: rounded-xl white card, neutral border,
  // soft shadow, 8px padding — matches the Gripp OS Popup.
  menu: {
    backgroundColor: role.canvas,
    minWidth: 200,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: 1,
    borderColor: role.borderControl,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
  },
  menuItem: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    borderRadius: tokens.borderRadius.sm,
  },
  menuItemHovered: {
    backgroundColor: role.surfaceHover,
  },
  menuItemTitle: {
    fontSize: 14,
    color: role.textStrong,
  },
}));
